import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormNode } from '../../../utils/api/aboboApiTypes';
import BaseQuestionType from './BaseQuestionType';

// Import shadcn components
import { DatePicker as DatePickerField } from '../../ui/date-picker';
import {
  BirthdateInput,
  BirthdateValue,
  DateFormat,
  stringToBirthdate,
  birthdateToString,
  validateBirthdate,
} from '../../ui/birthdate-input';
import QuestionHeading from '../../QuestionHeading';
import QuestionHelperText from '../../QuestionHelperText';

/**
 * Determines the date format based on locale.
 * - DMY: Most of Europe, Latin America, Australia, etc.
 * - YMD: China, Japan, Korea, etc.
 * - MDY: US, Philippines, some others
 */
function getDateFormatForLocale(locale: string): DateFormat {
  const lang = locale.toLowerCase().split('-')[0];

  // YMD locales (East Asian)
  const ymdLocales = ['zh', 'ja', 'ko', 'hu', 'lt', 'mn'];
  if (ymdLocales.includes(lang)) {
    return 'ymd';
  }

  // MDY locales (primarily US)
  const mdyLocales = ['en', 'tl', 'fil'];
  // Check for specific regional variants that use DMY
  const dmyEnVariants = ['en-gb', 'en-au', 'en-nz', 'en-ie', 'en-za'];
  if (dmyEnVariants.includes(locale.toLowerCase())) {
    return 'dmy';
  }
  if (mdyLocales.includes(lang)) {
    return 'mdy';
  }

  // Default to DMY (most common worldwide)
  return 'dmy';
}

interface DatePickerQuestionProps {
  node: FormNode;
}

export default forwardRef<any, DatePickerQuestionProps>(function DatePicker(
  { node },
  ref
) {
  const { i18n, t } = useTranslation();
  const inputMode = node.metadata?.dateInputMode;
  const dateFormat = useMemo(
    () => getDateFormatForLocale(i18n.language),
    [i18n.language]
  );

  // Defensive check for node
  if (!node) {
    console.error('DatePicker: node prop is undefined');
    return null;
  }

  const validateDate = (values: any[]) => {
    const value = values?.[0]?.data || '';

    if (node.metadata?.required && !value) {
      return t('questionTypes.datePicker.validation.required');
    }

    if (value) {
      let date;

      // Handle different date formats
      if (value.includes('/')) {
        // Handle MM/DD/YYYY format
        const parts = value.split('/');
        if (parts.length === 3) {
          const month = parseInt(parts[0], 10);
          const day = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          date = new Date(year, month - 1, day);
        }
      } else {
        date = new Date(value);
      }

      if (!date || isNaN(date.getTime())) {
        return t('questionTypes.datePicker.validation.invalidDate');
      }

      // Add any additional date validation here
      const today = new Date();
      today.setHours(23, 59, 59, 999); // Set to end of day for comparison
      if (date > today) {
        return t('questionTypes.datePicker.validation.futureDate');
      }
    }

    return true;
  };

  const validateBirthdateField = (values: any[]) => {
    const value = values?.[0]?.data || '';

    if (node.metadata?.required && !value) {
      return t('questionTypes.datePicker.validation.required');
    }

    if (value) {
      const birthdate = stringToBirthdate(value);
      const error = validateBirthdate(birthdate);
      if (error) {
        return error;
      }
    }

    return true;
  };

  // Memoize transforms based on input mode
  const calendarTransform = useMemo(
    () => ({
      input: (values: any[]) => {
        const value = values?.[0]?.data || '';

        // Convert to Date object for the DatePicker
        if (value) {
          // Handle different date formats
          let date;
          if (value.includes('/')) {
            // Handle MM/DD/YYYY format
            const parts = value.split('/');
            if (parts.length === 3) {
              const month = parseInt(parts[0], 10);
              const day = parseInt(parts[1], 10);
              const year = parseInt(parts[2], 10);
              date = new Date(year, month - 1, day);
            }
          } else {
            date = new Date(value);
          }

          if (date && !isNaN(date.getTime())) {
            return date;
          }
        }
        return null;
      },
      output: (value: Date | null) => {
        if (value && !isNaN(value.getTime())) {
          // Convert Date back to MM/DD/YYYY for storage
          const month = String(value.getMonth() + 1).padStart(2, '0');
          const day = String(value.getDate()).padStart(2, '0');
          const year = String(value.getFullYear());
          return [{ data: `${month}/${day}/${year}` }];
        }
        return [];
      },
    }),
    []
  );

  const birthdateTransform = useMemo(
    () => ({
      input: (values: any[]): BirthdateValue | null => {
        const dateStr = values?.[0]?.data || '';
        if (!dateStr) return null;
        // Always parse stored data as MDY format (internal storage format)
        return stringToBirthdate(dateStr, 'mdy');
      },
      output: (birthdate: BirthdateValue | null) => {
        // Always store as MDY format regardless of display format
        const dateStr = birthdateToString(birthdate, 'mdy');
        if (dateStr) {
          return [{ data: dateStr }];
        }
        return [];
      },
    }),
    []
  );

  if (inputMode === 'birthdate') {
    return (
      <BaseQuestionType
        node={node}
        validate={validateBirthdateField}
        transform={birthdateTransform}
      >
        {(field) => (
          <div className="space-y-4" ref={ref}>
            <QuestionHeading required={node.metadata?.required}>
              {node.body}
            </QuestionHeading>

            {node.helperText && (
              <QuestionHelperText>{node.helperText}</QuestionHelperText>
            )}

            <BirthdateInput
              value={field.value as BirthdateValue | null}
              onChange={(birthdate) => field.onChange(birthdate)}
              disabled={false}
              format={dateFormat}
            />

            {field.error && (
              <p className="text-red-500 text-sm text-center">{field.error}</p>
            )}
          </div>
        )}
      </BaseQuestionType>
    );
  }

  return (
    <BaseQuestionType
      node={node}
      validate={validateDate}
      transform={calendarTransform}
    >
      {(field) => (
        <div className="space-y-4" ref={ref}>
          <QuestionHeading required={node.metadata?.required}>
            {node.body}
          </QuestionHeading>

          {node.helperText && (
            <QuestionHelperText>{node.helperText}</QuestionHelperText>
          )}

          <DatePickerField
            {...({
              date: field.value as Date | null,
              setDate: (date: Date | null) => field.onChange(date),
              disabled: false,
              disabledDays: (date: Date) => date > new Date(), // Prevent future dates
              align: 'end',
              side: 'bottom',
              language: i18n.language,
              placeholder: t('questionTypes.datePicker.placeholder'),
              nlpPlaceholder: t('questionTypes.datePicker.nlpPlaceholder'),
            } as any)}
          />

          {field.error && (
            <p className="text-red-500 text-sm text-center">{field.error}</p>
          )}
        </div>
      )}
    </BaseQuestionType>
  );
});
